import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

//hooks
import useDateLimits from '@/utils/hooks/datepicker/useDateLimits';
import useUser from '@/utils/hooks/swr/useUser';

//Lib
import { localStorageDatepickerMentions } from '@/lib/analytics/global';
import datepickerDateFormat from '@/lib/datepicker/dateFormat';
import { LAST_30_DAYS, PREVIOUS_PERIOD } from '@/lib/periods';
import { INSTAGRAM } from '@/lib/socialProfile';

//components
import Datepicker from '@/components/base/Datepicker';
import DatepickerCompareTo from '@/components/base/Datepicker/DatepickerCompareTo';

dayjs.extend(utc);
dayjs.extend(timezone);

const DatepickerMentions = ({ periodSelected, setPeriodSelected, oldestMentionDate }) => {
    const { getMaxDate } = useDateLimits();
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const defaultMinDate = dayjs().tz(user?.settings.timezone).subtract(30, 'days').startOf('day');

    const mentionDate = oldestMentionDate
        ? dayjs(oldestMentionDate).tz(user?.settings.timezone)
        : null;

    const mentionDateIsBefore = mentionDate ? mentionDate.isBefore(defaultMinDate) : false; // default milliseconds

    const minDate = mentionDateIsBefore
        ? mentionDate.format('YYYY-MM-DDTHH:mm:ss')
        : defaultMinDate.format('YYYY-MM-DDTHH:mm:ss');

    useEffect(() => {
        localStorage.setItem(localStorageDatepickerMentions, JSON.stringify(periodSelected));
    }, [periodSelected]);

    return (
        <Datepicker
            date={periodSelected.date ?? null}
            periods="analytics"
            periodSelected={periodSelected.period ?? LAST_30_DAYS}
            timezone={user?.settings.timezone}
            bottomSection={
                <DatepickerCompareTo
                    compareTo={periodSelected.compareTo ?? PREVIOUS_PERIOD}
                    onChange={(el) => {
                        setPeriodSelected((prevState) => ({ ...prevState, compareTo: el.value }));
                    }}
                />
            }
            options={{
                placement: 'bottom-end',
                dateFormat: datepickerDateFormat[user.settings.date_format] ?? 'Y/m/d',
                mode: 'range',
                parseDate: (dateStr) => {
                    return dayjs(dateStr).toDate();
                },
                maxDate: getMaxDate({ social_network_type: INSTAGRAM }),
                minDate
            }}
            onChange={(data) => {
                if (data.date && data.date.length === 2) {
                    setPeriodSelected((prevState) => ({
                        ...prevState,
                        date: data.date,
                        period: data.periodSelected
                    }));
                }
            }}
        />
    );
};

DatepickerMentions.propTypes = {
    periodSelected: PropTypes.object,
    setPeriodSelected: PropTypes.func,
    oldestMentionDate: PropTypes.string
};

export default DatepickerMentions;
